import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { ReactElement } from 'react';
import getVideoId from 'get-video-id';
import Image from '@/lib/Image';

interface ListingImagesModalProps {
  images: {
    src: string;
    blurdata_url?: string | undefined | null;
    caption?: string | null | undefined;
  }[];
  video?: string | null;
  children: ({ onOpen }: Pick<UseDisclosureReturn, 'onOpen'>) => ReactElement;
}

const ListingImagesModal = ({
  images,
  video,
  children,
}: ListingImagesModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation('backoffice');
  const videoObj = video ? getVideoId(video) : null;

  return (
    <>
      {children({ onOpen })}
      <Modal isOpen={!!isOpen} onClose={onClose} size="5xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('listing_images', 'Kuvia kohteesta')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex flexDirection="column">
              {images.map((image, index) => (
                <Box mb={8} key={image.src}>
                  <Image
                    width="1080"
                    height="600"
                    objectFit="contain"
                    // sizes:
                    // since we're in a responsive modal
                    // - the max size of the images are 90vw when
                    //   smaller than 1024px (modal shrinks with viewport)
                    // - from 1024 upwards, the modal is at its max width and the images
                    //   won't be wider than 976px
                    sizes="(max-width: 1024px) 90vw,
                         976px"
                    src={image.src}
                    placeholder={image.blurdata_url ? 'blur' : 'empty'}
                    blurDataURL={image.blurdata_url || undefined}
                    alt={image.caption || `Image ${index}`}
                  />
                  {image.caption ? (
                    <Box textAlign="center" padding="4px 8px">
                      {image.caption}
                    </Box>
                  ) : null}
                </Box>
              ))}
              {videoObj && videoObj.service === 'youtube' && videoObj.id && (
                <Flex
                  height={['480px', null, 600]}
                  width="100%"
                  justifyContent="center"
                >
                  <iframe
                    title="Video"
                    height="100%"
                    width="100%"
                    src={`https://www.youtube.com/embed/${videoObj.id}`}
                    allowFullScreen
                  />
                </Flex>
              )}
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              {t('backoffice:close', 'Sulje')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ListingImagesModal;
